// Common Brazilian hosting providers. Values are editable in "Configurações avançadas".
export const EMAIL_PROVIDER_PRESETS = {
  hosting: {
    imapAddress: domain => (domain ? `mail.${domain}` : ''),
    smtpAddress: domain => (domain ? `mail.${domain}` : ''),
  },
  hostinger: {
    imapAddress: () => 'imap.hostinger.com',
    smtpAddress: () => 'smtp.hostinger.com',
  },
  titan: {
    imapAddress: () => 'imap.titan.email',
    smtpAddress: () => 'smtp.titan.email',
  },
  zoho: {
    imapAddress: () => 'imap.zoho.com',
    smtpAddress: () => 'smtp.zoho.com',
  },
  locaweb: {
    imapAddress: () => 'email-ssl.com.br',
    smtpAddress: () => 'email-ssl.com.br',
  },
};

export const emailDomain = email =>
  String(email || '')
    .split('@')[1]
    ?.trim()
    .toLowerCase() || '';

export const detectProvider = imapAddress => {
  const host = String(imapAddress || '').toLowerCase();
  if (!host) return 'hosting';
  if (host.includes('hostinger')) return 'hostinger';
  if (host.includes('titan.email')) return 'titan';
  if (host.includes('zoho')) return 'zoho';
  if (host.includes('email-ssl.com.br')) return 'locaweb';
  if (host.startsWith('mail.')) return 'hosting';
  return 'custom';
};

// Applies a preset keeping the standard secure ports (IMAP 993 SSL, SMTP 465 SSL/TLS).
export const applyProviderPreset = (state, provider, email) => {
  const preset = EMAIL_PROVIDER_PRESETS[provider];
  if (!preset) return state;
  const domain = emailDomain(email);
  return {
    ...state,
    imapAddress: preset.imapAddress(domain),
    imapPort: 993,
    imapSsl: true,
    smtpAddress: preset.smtpAddress(domain),
    smtpPort: 465,
    smtpSecurity: 'ssl',
    smtpDomain: state.smtpDomain || domain,
  };
};

export const stateFromInbox = inbox => {
  let smtpSecurity = 'none';
  if (inbox.smtp_enable_ssl_tls) smtpSecurity = 'ssl';
  else if (inbox.smtp_enable_starttls_auto) smtpSecurity = 'starttls';
  const login = inbox.imap_login || inbox.email || '';
  return {
    receiveEnabled: Boolean(inbox.imap_enabled),
    sendEnabled: inbox.imap_enabled ? Boolean(inbox.smtp_enabled) : true,
    login,
    password: '',
    sameCredentials: !inbox.smtp_login || inbox.smtp_login === login,
    smtpLogin: inbox.smtp_login || '',
    smtpPassword: '',
    imapAddress: inbox.imap_address || '',
    imapPort: inbox.imap_port || 993,
    imapSsl: inbox.imap_address ? Boolean(inbox.imap_enable_ssl) : true,
    imapAuthentication: inbox.imap_authentication || 'login',
    smtpAddress: inbox.smtp_address || '',
    smtpPort: inbox.smtp_port || 465,
    smtpSecurity: inbox.smtp_address ? smtpSecurity : 'ssl',
    smtpDomain: inbox.smtp_domain || emailDomain(login),
    smtpVerifyMode: inbox.smtp_openssl_verify_mode || 'none',
    smtpAuthentication: inbox.smtp_authentication || 'login',
  };
};

// Blank passwords are omitted so the server keeps the saved ones.
export const buildEmailConnectionPayload = state => {
  if (!state.receiveEnabled) {
    return { imap_enabled: false, smtp_enabled: false };
  }
  const channel = {
    imap_enabled: true,
    imap_address: state.imapAddress.trim(),
    imap_port: Number(state.imapPort),
    imap_login: state.login.trim(),
    imap_enable_ssl: state.imapSsl,
    imap_authentication: state.imapAuthentication,
    smtp_enabled: state.sendEnabled,
  };
  if (state.password) channel.imap_password = state.password;
  if (!state.sendEnabled) return channel;

  Object.assign(channel, {
    smtp_address: state.smtpAddress.trim(),
    smtp_port: Number(state.smtpPort),
    smtp_login: state.sameCredentials
      ? state.login.trim()
      : state.smtpLogin.trim(),
    smtp_domain: state.smtpDomain.trim(),
    smtp_enable_ssl_tls: state.smtpSecurity === 'ssl',
    smtp_enable_starttls_auto: state.smtpSecurity === 'starttls',
    smtp_openssl_verify_mode: state.smtpVerifyMode,
    smtp_authentication: state.smtpAuthentication,
  });
  const smtpPassword = state.sameCredentials
    ? state.password
    : state.smtpPassword;
  if (smtpPassword) channel.smtp_password = smtpPassword;
  return channel;
};

// Returns an i18n key for the first missing requirement, or '' when the form can be saved.
export const validateEmailConnection = (state, inbox) => {
  if (!state.receiveEnabled) return '';
  if (!state.login.trim()) return 'LOGIN_REQUIRED';
  if (!state.imapAddress.trim() || !state.imapPort) return 'IMAP_REQUIRED';
  if (!state.password && !inbox.imap_password_configured)
    return 'PASSWORD_REQUIRED';
  if (!state.sendEnabled) return '';
  if (!state.smtpAddress.trim() || !state.smtpPort) return 'SMTP_REQUIRED';
  if (state.sameCredentials) {
    if (!state.password && !inbox.smtp_password_configured)
      return 'PASSWORD_REQUIRED';
    return '';
  }
  if (!state.smtpLogin.trim()) return 'SMTP_LOGIN_REQUIRED';
  if (!state.smtpPassword && !inbox.smtp_password_configured)
    return 'SMTP_PASSWORD_REQUIRED';
  return '';
};
